import { useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alertwhale/ui'
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '@/hooks/useApiKeys'
import { useApplications } from '@/hooks/useApplications'
import {
  Key,
  Plus,
  Loader2,
  Copy,
  Trash2,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Clock,
  Shield,
} from 'lucide-react'
import { formatDate, copyToClipboard } from '@/lib/utils'
import type { ApiKeyScope } from '@alertwhale/shared'
import { toast } from 'sonner'

const SCOPE_GROUPS = [
  {
    name: 'Notifications',
    scopes: [
      { value: 'notifications:send' as ApiKeyScope, label: 'Send Notifications', description: 'Send push notifications to users' },
      { value: 'notifications:read' as ApiKeyScope, label: 'Read Notifications', description: 'View notification history' },
    ],
  },
  {
    name: 'Live Sessions',
    scopes: [
      { value: 'live:create' as ApiKeyScope, label: 'Create Live Sessions', description: 'Start new live activities' },
      { value: 'live:update' as ApiKeyScope, label: 'Update Live Sessions', description: 'Update existing live activities' },
      { value: 'live:read' as ApiKeyScope, label: 'Read Live Sessions', description: 'View live session data' },
    ],
  },
  {
    name: 'Users & Devices',
    scopes: [
      { value: 'users:write' as ApiKeyScope, label: 'Manage Users', description: 'Create and update users' },
      { value: 'users:read' as ApiKeyScope, label: 'Read Users', description: 'View user data' },
      { value: 'devices:write' as ApiKeyScope, label: 'Manage Devices', description: 'Register and remove devices' },
      { value: 'devices:read' as ApiKeyScope, label: 'Read Devices', description: 'View device tokens' },
    ],
  },
]

const EXPIRATION_OPTIONS = [
  { value: 'never', label: 'Never expires' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
]

export function ApiKeysPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isRevokeOpen, setIsRevokeOpen] = useState(false)
  const [keyToRevoke, setKeyToRevoke] = useState<{ id: string; name: string } | null>(null)
  const [name, setName] = useState('')
  const [applicationId, setApplicationId] = useState<string>('all')
  const [expiresInDays, setExpiresInDays] = useState<string>('never')
  const [selectedScopes, setSelectedScopes] = useState<ApiKeyScope[]>([])
  const [newKey, setNewKey] = useState<string | null>(null)
  const [showKey, setShowKey] = useState(false)
  const [copied, setCopied] = useState(false)

  const { data: apiKeys, isLoading } = useApiKeys()
  const { data: applications } = useApplications()
  const createKey = useCreateApiKey()
  const revokeKey = useRevokeApiKey()

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const result = await createKey.mutateAsync({
        name,
        scopes: selectedScopes,
        application_id: applicationId !== 'all' ? applicationId : undefined,
        expires_in_days: expiresInDays !== 'never' ? parseInt(expiresInDays) : undefined,
      })
      setNewKey(result.key)
      toast.success('API key created successfully')
    } catch {
      toast.error('Failed to create API key')
    }
  }

  const handleCloseCreate = () => {
    setIsCreateOpen(false)
    setName('')
    setApplicationId('all')
    setExpiresInDays('never')
    setSelectedScopes([])
    setNewKey(null)
    setShowKey(false)
    setCopied(false)
  }

  const handleRevoke = async () => {
    if (!keyToRevoke) return
    try {
      await revokeKey.mutateAsync(keyToRevoke.id)
      toast.success('API key revoked successfully')
      setIsRevokeOpen(false)
      setKeyToRevoke(null)
    } catch {
      toast.error('Failed to revoke API key')
    }
  }

  const toggleScope = (scope: ApiKeyScope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    )
  }

  const selectAllInGroup = (groupScopes: { value: ApiKeyScope }[]) => {
    const scopeValues = groupScopes.map((s) => s.value)
    const allSelected = scopeValues.every((s) => selectedScopes.includes(s))
    if (allSelected) {
      setSelectedScopes((prev) => prev.filter((s) => !scopeValues.includes(s)))
    } else {
      setSelectedScopes((prev) => [...new Set([...prev, ...scopeValues])])
    }
  }

  const handleCopy = async () => {
    if (newKey) {
      await copyToClipboard(newKey)
      setCopied(true)
      toast.success('API key copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const keys = apiKeys || []

  const isExpiringSoon = (expiresAt: string | null) => {
    if (!expiresAt) return false
    const daysUntilExpiry = Math.ceil(
      (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    )
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0
  }

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false
    return new Date(expiresAt) < new Date()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            <span className="text-gradient">API Keys</span>
          </h1>
          <p className="text-muted-foreground mt-1">Manage API keys to authenticate your requests</p>
        </div>
        <Button variant="glow" onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Create API Key
        </Button>
      </div>

      {/* Security Notice */}
      <div className="glass-card rounded-xl p-5 border-warning/30 bg-warning/5">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-warning/20 text-warning">
            <Shield className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-warning">Keep your API keys secure</h3>
            <p className="text-sm text-muted-foreground">
              Never share your API keys publicly or commit them to source control. Use environment
              variables to store them securely.
            </p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading API keys...</span>
          </div>
        </div>
      ) : keys.length === 0 ? (
        <Card className="glass-card border-0">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Key className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No API keys</h3>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
              Create an API key to authenticate your requests to the AlertWhale API
            </p>
            <Button variant="glow" onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              Create Your First API Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="glass-card border-0">
          <CardHeader>
            <CardTitle className="text-lg">
              Your API Keys
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({keys.length} active)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {keys.map((key) => (
                <div
                  key={key.id}
                  className={`flex items-start justify-between p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-primary/20 transition-all group ${
                    isExpired(key.expires_at)
                      ? 'border-destructive/30 bg-destructive/5'
                      : isExpiringSoon(key.expires_at)
                        ? 'border-warning/30 bg-warning/5'
                        : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Key className="w-6 h-6 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold group-hover:text-primary transition-colors">{key.name}</p>
                        {isExpired(key.expires_at) && (
                          <Badge variant="destructive" className="text-xs">
                            Expired
                          </Badge>
                        )}
                        {isExpiringSoon(key.expires_at) && !isExpired(key.expires_at) && (
                          <Badge variant="outline" className="text-xs text-warning bg-warning/10 border-warning/20">
                            Expiring soon
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground font-mono">{key.key_prefix}...</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        <span>Created {formatDate(key.created_at)}</span>
                        {key.last_used_at && <span>• Last used {formatDate(key.last_used_at)}</span>}
                        {key.expires_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Expires {formatDate(key.expires_at)}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1 flex-wrap mt-2">
                        {key.scopes.map((scope) => (
                          <Badge key={scope} variant="outline" className="text-xs bg-white/[0.03]">
                            {scope}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setKeyToRevoke({ id: key.id, name: key.name })
                      setIsRevokeOpen(true)
                    }}
                    className="shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={handleCloseCreate}>
        <DialogContent className="glass-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{newKey ? 'API Key Created' : 'Create API Key'}</DialogTitle>
            <DialogDescription>
              {newKey
                ? "Copy your API key now. You won't be able to see it again."
                : 'Create a new API key with specific permissions'}
            </DialogDescription>
          </DialogHeader>

          {newKey ? (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between gap-2">
                  <code className="text-sm break-all flex-1">
                    {showKey ? newKey : '•'.repeat(40)}
                  </code>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => setShowKey(!showKey)}>
                      {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleCopy}>
                      {copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 text-sm text-amber-600 bg-amber-500/10 p-3 rounded-lg">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>
                  Make sure to copy your API key now. You won't be able to see it again!
                </p>
              </div>
              <Button onClick={handleCloseCreate} className="w-full">
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleCreate}>
              <div className="space-y-6 py-4">
                {/* Key Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Key Name *</Label>
                  <Input
                    id="name"
                    placeholder="Production API Key"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    A descriptive name to identify this key
                  </p>
                </div>

                {/* Application (Optional) */}
                <div className="space-y-2">
                  <Label>Application (Optional)</Label>
                  <Select value={applicationId} onValueChange={setApplicationId}>
                    <SelectTrigger>
                      <SelectValue placeholder="All applications" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All applications</SelectItem>
                      {applications?.map((app) => (
                        <SelectItem key={app.id} value={app.id}>
                          {app.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Restrict this key to a specific application
                  </p>
                </div>

                {/* Expiration */}
                <div className="space-y-2">
                  <Label>Expiration</Label>
                  <Select value={expiresInDays} onValueChange={setExpiresInDays}>
                    <SelectTrigger>
                      <SelectValue placeholder="Never expires" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPIRATION_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Permissions */}
                <div className="space-y-4">
                  <Label>Permissions *</Label>
                  {SCOPE_GROUPS.map((group) => (
                    <div key={group.name} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">
                          {group.name}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto py-1 px-2 text-xs"
                          onClick={() => selectAllInGroup(group.scopes)}
                        >
                          {group.scopes.every((s) => selectedScopes.includes(s.value))
                            ? 'Deselect all'
                            : 'Select all'}
                        </Button>
                      </div>
                      <div className="grid gap-2">
                        {group.scopes.map((scope) => (
                          <label
                            key={scope.value}
                            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                              selectedScopes.includes(scope.value)
                                ? 'bg-primary/5 border-primary'
                                : 'hover:bg-muted'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedScopes.includes(scope.value)}
                              onChange={() => toggleScope(scope.value)}
                              className="mt-0.5 rounded"
                            />
                            <div>
                              <span className="text-sm font-medium">{scope.label}</span>
                              <p className="text-xs text-muted-foreground">{scope.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseCreate}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createKey.isPending || selectedScopes.length === 0}>
                  {createKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Key
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={isRevokeOpen} onOpenChange={setIsRevokeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API Key</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke <strong>{keyToRevoke?.name}</strong>? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg my-4">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              Any applications using this key will immediately lose access to the API.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRevokeOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revokeKey.isPending}
            >
              {revokeKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Revoke Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
